import React, { useEffect, useState, useCallback } from "react";
import {
  UserPlus, Pencil, Trash2, KeyRound, ToggleLeft, ToggleRight,
  Search, X, Shield, Users, UserCheck, AlertCircle,
} from "lucide-react";
import { toast } from "react-toastify";
import api from "../api/api";
import "../css/userManagement.css";

const ROLE_OPTIONS = [
  { value: "agent", label: "Agent" },
  { value: "admin", label: "Manager (Admin)" },
];

const ROLE_LABEL = { superadmin: "Super Admin", admin: "Manager", agent: "Agent" };
const ROLE_COLOR = { superadmin: "warning", admin: "info", agent: "success" };

const EMPTY_FORM = { Name: "", email: "", password: "", phone: "", role: "agent" };

export default function UserManagement() {
  const currentRole = localStorage.getItem("role") || "agent";

  const [users,       setUsers]       = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [search,      setSearch]      = useState("");
  const [roleFilter,  setRoleFilter]  = useState("");
  const [showModal,   setShowModal]   = useState(false);
  const [editingUser, setEditingUser] = useState(null);   // null = create mode
  const [form,        setForm]        = useState(EMPTY_FORM);
  const [formErrors,  setFormErrors]  = useState({});
  const [submitting,  setSubmitting]  = useState(false);
  const [showPwModal, setShowPwModal] = useState(null);   // userId
  const [newPassword, setNewPassword] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState(null); // userId

  // ── Fetch users ─────────────────────────────────────────────────────────
  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get("/users");
      setUsers(res.data || []);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load users");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  // ── Derived list ─────────────────────────────────────────────────────────
  const filtered = users.filter(u => {
    const matchSearch = !search ||
      u.Name?.toLowerCase().includes(search.toLowerCase()) ||
      u.email?.toLowerCase().includes(search.toLowerCase());
    const matchRole = !roleFilter || u.role === roleFilter;
    return matchSearch && matchRole;
  });

  // ── Stats ────────────────────────────────────────────────────────────────
  const stats = {
    total:  users.length,
    active: users.filter(u => u.isActive !== false).length,
    agents: users.filter(u => u.role === "agent").length,
    admins: users.filter(u => u.role === "admin").length,
  };

  // ── Form validation ──────────────────────────────────────────────────────
  const validate = (f, isCreate) => {
    const errs = {};
    if (!f.Name?.trim())  errs.Name  = "Full name is required";
    if (!f.email?.trim()) errs.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(f.email)) errs.email = "Invalid email format";
    if (isCreate && (!f.password || f.password.length < 6))
      errs.password = "Password must be at least 6 characters";
    return errs;
  };

  const openCreate = () => {
    setEditingUser(null);
    setForm(EMPTY_FORM);
    setFormErrors({});
    setShowModal(true);
  };

  const openEdit = (user) => {
    setEditingUser(user);
    setForm({ Name: user.Name || "", email: user.email, phone: user.phone || "", role: user.role || "agent", password: "" });
    setFormErrors({});
    setShowModal(true);
  };

  const closeModal = () => { setShowModal(false); setEditingUser(null); setForm(EMPTY_FORM); };

  // ── Save (create or update) ──────────────────────────────────────────────
  const handleSave = async (e) => {
    e.preventDefault();
    const isCreate = !editingUser;
    const errs = validate(form, isCreate);
    if (Object.keys(errs).length) { setFormErrors(errs); return; }

    setSubmitting(true);
    try {
      if (isCreate) {
        await api.post("/users", form);
        toast.success("User created successfully");
      } else {
        const payload = { Name: form.Name, phone: form.phone, role: form.role };
        await api.put(`/users/${editingUser._id}`, payload);
        toast.success("User updated successfully");
      }
      closeModal();
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.message || "Operation failed");
    } finally {
      setSubmitting(false);
    }
  };

  // ── Toggle active ────────────────────────────────────────────────────────
  const toggleActive = async (user) => {
    try {
      await api.put(`/users/${user._id}`, { isActive: !user.isActive });
      toast.success(`User ${!user.isActive ? "activated" : "deactivated"}`);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update status");
    }
  };

  // ── Delete ───────────────────────────────────────────────────────────────
  const handleDelete = async (id) => {
    try {
      await api.delete(`/users/${id}`);
      toast.success("User deleted");
      setDeleteConfirm(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to delete user");
    }
  };

  // ── Reset password ───────────────────────────────────────────────────────
  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error("Password must be at least 6 characters"); return;
    }
    try {
      await api.patch(`/users/${showPwModal}/reset-password`, { newPassword });
      toast.success("Password reset successfully");
      setShowPwModal(null);
      setNewPassword("");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to reset password");
    }
  };

  // ── Guard: only superadmin/admin can access ──────────────────────────────
  if (currentRole === "agent") {
    return (
      <div className="um-access-denied">
        <AlertCircle size={48} color="#EF4444" />
        <h2>Access Denied</h2>
        <p>You do not have permission to view this page.</p>
      </div>
    );
  }

  return (
    <div className="um-container">
      {/* ── Header ── */}
      <div className="um-header">
        <div>
          <h1 className="um-title">User Management</h1>
          <p className="um-subtitle">Manage team members, roles, and access</p>
        </div>
        <button className="btn btn-primary" onClick={openCreate}>
          <UserPlus size={16} />
          Add User
        </button>
      </div>

      {/* ── Stats ── */}
      <div className="um-stats-row">
        {[
          { label: "Total Users",   value: stats.total,  icon: Users,     color: "#1D4ED8" },
          { label: "Active",        value: stats.active, icon: UserCheck, color: "#10B981" },
          { label: "Agents",        value: stats.agents, icon: Shield,    color: "#8B5CF6" },
          { label: "Managers",      value: stats.admins, icon: Shield,    color: "#F59E0B" },
        ].map(({ label, value, icon: Icon, color }) => (
          <div className="um-stat-card" key={label}>
            <div className="um-stat-icon" style={{ background: color + "18", color }}>
              <Icon size={20} />
            </div>
            <div>
              <p className="um-stat-value">{value}</p>
              <p className="um-stat-label">{label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* ── Filters ── */}
      <div className="um-filters">
        <div className="um-search-box">
          <Search size={15} color="#94A3B8" />
          <input
            type="text"
            placeholder="Search by name or email…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {search && <button onClick={() => setSearch("")}><X size={14} /></button>}
        </div>
        <select className="um-select" value={roleFilter} onChange={e => setRoleFilter(e.target.value)}>
          <option value="">All Roles</option>
          <option value="superadmin">Super Admin</option>
          <option value="admin">Manager</option>
          <option value="agent">Agent</option>
        </select>
      </div>

      {/* ── Table ── */}
      <div className="um-table-card">
        {loading ? (
          <div className="um-loading">Loading users…</div>
        ) : filtered.length === 0 ? (
          <div className="um-empty">
            <Users size={40} color="#CBD5E1" />
            <p>No users found</p>
          </div>
        ) : (
          <table className="um-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Role</th>
                <th>Phone</th>
                <th>Status</th>
                <th>Joined</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(user => (
                <tr key={user._id}>
                  <td>
                    <div className="um-user-cell">
                      <div className="um-avatar">{(user.Name || "U").charAt(0).toUpperCase()}</div>
                      <div>
                        <p className="um-user-name">{user.Name || "—"}</p>
                        <p className="um-user-email">{user.email}</p>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className={`badge badge-${ROLE_COLOR[user.role] || "muted"}`}>
                      {ROLE_LABEL[user.role] || user.role}
                    </span>
                  </td>
                  <td className="um-phone">{user.phone || "—"}</td>
                  <td>
                    <span className={`badge ${user.isActive !== false ? "badge-success" : "badge-danger"}`}>
                      {user.isActive !== false ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="um-date">
                    {user.createdAt ? new Date(user.createdAt).toLocaleDateString("en-IN") : "—"}
                  </td>
                  <td>
                    <div className="um-actions">
                      <button
                        className="um-action-btn edit"
                        title="Edit user"
                        onClick={() => openEdit(user)}
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        className="um-action-btn key"
                        title="Reset password"
                        onClick={() => { setShowPwModal(user._id); setNewPassword(""); }}
                      >
                        <KeyRound size={14} />
                      </button>
                      <button
                        className={`um-action-btn ${user.isActive !== false ? "deactivate" : "activate"}`}
                        title={user.isActive !== false ? "Deactivate" : "Activate"}
                        onClick={() => toggleActive(user)}
                      >
                        {user.isActive !== false ? <ToggleRight size={16} /> : <ToggleLeft size={16} />}
                      </button>
                      {currentRole === "superadmin" && (
                        <button
                          className="um-action-btn delete"
                          title="Delete user"
                          onClick={() => setDeleteConfirm(user._id)}
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Create / Edit Modal ── */}
      {showModal && (
        <div className="um-modal-overlay" onClick={closeModal}>
          <div className="um-modal" onClick={e => e.stopPropagation()}>
            <div className="um-modal-header">
              <h3>{editingUser ? "Edit User" : "Add New User"}</h3>
              <button className="um-modal-close" onClick={closeModal}><X size={18} /></button>
            </div>
            <form onSubmit={handleSave} className="um-form">
              <div className="um-form-row">
                <div className="um-form-group">
                  <label>Full Name *</label>
                  <input
                    type="text"
                    value={form.Name}
                    onChange={e => setForm({ ...form, Name: e.target.value })}
                    placeholder="Enter full name"
                  />
                  {formErrors.Name && <span className="um-err">{formErrors.Name}</span>}
                </div>
                <div className="um-form-group">
                  <label>Phone</label>
                  <input
                    type="text"
                    value={form.phone}
                    onChange={e => setForm({ ...form, phone: e.target.value })}
                    placeholder="+91 XXXXX XXXXX"
                  />
                </div>
              </div>

              <div className="um-form-group">
                <label>Email Address *</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={e => setForm({ ...form, email: e.target.value })}
                  placeholder="user@zenrealty.co"
                  disabled={!!editingUser}
                />
                {formErrors.email && <span className="um-err">{formErrors.email}</span>}
              </div>

              {!editingUser && (
                <div className="um-form-group">
                  <label>Password *</label>
                  <input
                    type="password"
                    value={form.password}
                    onChange={e => setForm({ ...form, password: e.target.value })}
                    placeholder="Minimum 6 characters"
                  />
                  {formErrors.password && <span className="um-err">{formErrors.password}</span>}
                </div>
              )}

              <div className="um-form-group">
                <label>Role *</label>
                <select
                  value={form.role}
                  onChange={e => setForm({ ...form, role: e.target.value })}
                >
                  {ROLE_OPTIONS.map(r => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
              </div>

              <div className="um-modal-footer">
                <button type="button" className="btn btn-outline" onClick={closeModal}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? "Saving…" : editingUser ? "Save Changes" : "Create User"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Reset Password Modal ── */}
      {showPwModal && (
        <div className="um-modal-overlay" onClick={() => setShowPwModal(null)}>
          <div className="um-modal um-modal-sm" onClick={e => e.stopPropagation()}>
            <div className="um-modal-header">
              <h3>Reset Password</h3>
              <button className="um-modal-close" onClick={() => setShowPwModal(null)}><X size={18} /></button>
            </div>
            <div className="um-form">
              <div className="um-form-group">
                <label>New Password</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  placeholder="Minimum 6 characters"
                  autoFocus
                />
              </div>
              <div className="um-modal-footer">
                <button className="btn btn-outline" onClick={() => setShowPwModal(null)}>Cancel</button>
                <button className="btn btn-primary" onClick={handleResetPassword}>Reset Password</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Confirm Modal ── */}
      {deleteConfirm && (
        <div className="um-modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="um-modal um-modal-sm" onClick={e => e.stopPropagation()}>
            <div className="um-modal-header">
              <h3>Delete User</h3>
              <button className="um-modal-close" onClick={() => setDeleteConfirm(null)}><X size={18} /></button>
            </div>
            <div className="um-form">
              <p className="um-confirm-text">
                This action is permanent and cannot be undone. Are you sure you want to delete this user?
              </p>
              <div className="um-modal-footer">
                <button className="btn btn-outline" onClick={() => setDeleteConfirm(null)}>Cancel</button>
                <button className="btn btn-danger" onClick={() => handleDelete(deleteConfirm)}>
                  <Trash2 size={14} /> Delete User
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
